"use client";

import { cn } from "../lib/utils";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// Loading skeletons (F180): grey shapes the size of the content they stand in
// for, so a page keeps its shape while it loads. Rules:
// - nothing for the first SHOW_AFTER ms (a quick load shows no skeleton at
//   all), then a fade-in (`Loading`, CSS .skeleton-delay);
// - once shown, at least MIN_SHOWN ms (`useLoaded`), so it never flashes;
// - the shimmer is an opacity pulse (no gradient; cheap to animate).
// Blocks are a dark wash on whatever is behind them (cards, the canvas).

const SHOW_AFTER = 150;
const MIN_SHOWN = 300;

/**
 * `value` once loaded, held back so a skeleton that has appeared stays at
 * least MIN_SHOWN ms; null while loading. Later changes (a reload) pass
 * straight through.
 */
export function useLoaded<T>(value: T | null | undefined): T | null {
  const [start] = useState(() => Date.now());
  const [shown, setShown] = useState<T | null>(value ?? null);
  useEffect(() => {
    if (value == null) return setShown(null);
    const elapsed = Date.now() - start;
    const wait = elapsed < SHOW_AFTER ? 0 : SHOW_AFTER + MIN_SHOWN - elapsed;
    if (wait <= 0) return setShown(value);
    const timer = setTimeout(() => setShown(value), wait);
    return () => clearTimeout(timer);
  }, [value, start]);
  return shown;
}

/** One grey shape. Size and corners come from `className`. */
export function SkeletonBlock({ className }: { className?: string }) {
  return <span aria-hidden className={cn("skeleton-block bg-foreground/[0.07] block rounded-lg", className)} />;
}

/**
 * True once loading has given up (landing's /app on a weak network, F203):
 * every skeleton then says «بارگذاری نشد» (a dash in a line of text) instead of
 * pulsing on; the page offers the retry. Without a provider (admin) nothing changes.
 */
export const LoadFailure = createContext(false);

/** The wrapper every skeleton sits in: announced as loading, shown after SHOW_AFTER. */
export function Loading({ className, inline = false, children }: { className?: string; inline?: boolean; children: ReactNode }) {
  const failed = useContext(LoadFailure);
  const Tag = inline ? "span" : "div";
  // Not `className`: it shapes the skeleton (grids, heights), not this line.
  if (failed) return inline ? <span className="text-muted-foreground">—</span> : <p className="text-muted-foreground py-2 text-sm">بارگذاری نشد</p>;
  return (
    <Tag role="status" aria-label="در حال بارگذاری" className={cn("skeleton-delay", inline ? "inline-block align-middle" : "flex flex-col gap-3", className)}>
      {children}
    </Tag>
  );
}

/** A short bar in a line of text (a balance, a count). */
export function InlineSkeleton({ className }: { className?: string }) {
  return (
    <Loading inline>
      <SkeletonBlock className={cn("h-[1em] w-16", className)} />
    </Loading>
  );
}

/** List rows like the order and ticket cards: two lines at the start, two at the end. */
export function ListSkeleton({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <Loading className={className}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="bg-background flex items-center justify-between gap-3 rounded-card p-4 shadow-sm">
          <div className="flex flex-col gap-2">
            <SkeletonBlock className="h-4 w-24" />
            <SkeletonBlock className="h-3 w-32" />
          </div>
          <div className="flex flex-col items-end gap-2">
            <SkeletonBlock className="h-4 w-20" />
            <SkeletonBlock className="h-5 w-16 rounded-full" />
          </div>
        </div>
      ))}
    </Loading>
  );
}

/** A panel (panelClass): the icon tile and title, then `lines` lines of text. */
export function PanelSkeleton({ lines = 2, className }: { lines?: number; className?: string }) {
  return (
    <Loading className={className}>
      <div className="bg-background flex flex-col gap-4 rounded-card p-4 shadow-sm sm:p-5">
        <div className="flex items-center gap-3">
          <SkeletonBlock className="size-10 shrink-0 rounded-2xl" />
          <div className="flex flex-1 flex-col gap-2">
            <SkeletonBlock className="h-4 w-28" />
            <SkeletonBlock className="h-3 w-44" />
          </div>
        </div>
        {Array.from({ length: lines }, (_, i) => (
          <SkeletonBlock key={i} className={cn("h-3.5", i === lines - 1 ? "w-2/3" : "w-full")} />
        ))}
      </div>
    </Loading>
  );
}

/** Rows inside a panel (addresses, Shebas, devices): an icon tile and two lines each. */
export function RowsSkeleton({ rows = 2, icon = true, className }: { rows?: number; icon?: boolean; className?: string }) {
  return (
    <Loading className={className}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          {icon ? <SkeletonBlock className="size-10 shrink-0 rounded-2xl" /> : null}
          <div className="flex flex-1 flex-col gap-2">
            <SkeletonBlock className="h-3.5 w-1/2" />
            <SkeletonBlock className="h-3 w-3/4" />
          </div>
        </div>
      ))}
    </Loading>
  );
}
